import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../layout/Layout";
import DataGrid from "react-data-grid";
import DatePicker from "react-datepicker";
import Spinner from "../layout/Spinner";
import { CSVLink } from "react-csv";
import { api } from "../utils/Api";
import formatDate from "../utils/FormatDate";
import { toCurrency } from "../utils/Convert";

const parseNumeric = (val) => {
	if (typeof val === "number") return val;
	if (typeof val === "string")
		return parseFloat(val.replace(/\$/g, "").replace(/,/g, "")) || 0;
	return 0;
};

const columna = [
	{ key: "locality",         name: "Locality",        resizable: true },
	{ key: "totalJobs",        name: "Total Jobs",       resizable: true, sortable: true },
	{ key: "jobsSanBenito",    name: "San Benito Job",   resizable: true, sortable: true },
	{ key: "jobsBrownsville",  name: "Brownsville Job",  resizable: true, sortable: true },
	{ key: "jobsMission",      name: "Mission Job",      resizable: true, sortable: true },
	{ key: "yardsSanBenito",   name: "San Benito CY",    resizable: true, sortable: true },
	{ key: "yardsBrownsville", name: "Brownsville CY",   resizable: true, sortable: true },
	{ key: "yardsMission",     name: "Mission CY",       resizable: true, sortable: true },
	{ key: "totalCY",          name: "Total CY",         resizable: true, sortable: true },
	{ key: "amount",           name: "Amount ($)",       resizable: true, sortable: true, formatter: ({ row }) => <div style={{ textAlign: "right" }}>{row.amount}</div> },
];

const TopLocalities = () => {
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(new Date());
	const [dateChange, setDateChange] = useState(true);
	const [loading, setLoading] = useState(false);
	const [limit, setLimit] = useState(50);
	const [rows, setRows] = useState([]);
	const [allRows, setAllRows] = useState([]);
	const [sortedLocalities, setSortedLocalities] = useState([]);
	const [sortColumns, setSortColumns] = useState([
		{ key: "totalJobs",        direction: "ASC" },
		{ key: "jobsSanBenito",    direction: "ASC" },
		{ key: "jobsBrownsville",  direction: "ASC" },
		{ key: "jobsMission",      direction: "ASC" },
		{ key: "yardsSanBenito",   direction: "ASC" },
		{ key: "yardsBrownsville", direction: "ASC" },
		{ key: "yardsMission",     direction: "ASC" },
		{ key: "totalCY",          direction: "ASC" },
		{ key: "amount",           direction: "ASC" },
	]);

	const headers = () =>
		columna.map((item) => ({ key: item.key, label: item.name }));

	useEffect(() => {
		let fechaIni = formatDate(startDate);
		let fechaFin = formatDate(endDate);

		if (endDate < startDate) {
			setEndDate(startDate);
			fechaFin = formatDate(startDate);
		}

		if (dateChange) {
			getLocalities(fechaIni, fechaFin);
		}
	}, [startDate, endDate]);

	const getLocalities = async (startDate, endDate) => {
		const url =
			api + `/joblocation/57Concrete?startDate=${startDate}&endDate=${endDate}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoading(true);
			const { data } = await axios.get(url);
			console.log(data)
			getTotals(data);
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const getTotals = (jobs) => {
		const rawMap = new Map();

		jobs.forEach((job) => {
			const plant     = job.plant     || "";
			const loadYD3   = job.loadYD3   || 0;
			const unitPrice = job.unitPrice  || 0;
			const locality  = job.locality   || "NA";

			if (!rawMap.has(locality)) {
				rawMap.set(locality, {
					totalYards: 0,
					jobsSanBenito: 0,   jobsBrownsville: 0,   jobsMission: 0,
					yardsSanBenito: 0,  yardsBrownsville: 0,  yardsMission: 0,
					amount: 0,
				});
			}
			const e = rawMap.get(locality);
			e.totalYards += loadYD3;
			e.amount     += unitPrice * loadYD3;

			if (plant === "San Benito")       { e.jobsSanBenito++;   e.yardsSanBenito   += loadYD3; }
			else if (plant === "Brownsville") { e.jobsBrownsville++; e.yardsBrownsville += loadYD3; }
			else if (plant === "Mission")     { e.jobsMission++;     e.yardsMission     += loadYD3; }
		});

		const localities = Array.from(rawMap.entries()).sort(
			(a, b) => b[1].totalYards - a[1].totalYards
		);
		const localityNames = localities.map(([name]) => name);

		const fmt = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

		const flat = localities.map(([locality, e]) => ({
			locality,
			totalJobs:        e.jobsSanBenito + e.jobsBrownsville + e.jobsMission,
			jobsSanBenito:    e.jobsSanBenito,
			jobsBrownsville:  e.jobsBrownsville,
			jobsMission:      e.jobsMission,
			yardsSanBenito:   fmt(e.yardsSanBenito),
			yardsBrownsville: fmt(e.yardsBrownsville),
			yardsMission:     fmt(e.yardsMission),
			totalCY:          fmt(e.totalYards),
			amount:           toCurrency(e.amount),
		}));

		setSortedLocalities(localityNames);
		setAllRows(flat);

		const topN = new Set(localityNames.slice(0, limit));
		setRows(flat.filter((r) => topN.has(r.locality)));
	};

	const changeLimit = (x) => {
		const n = parseInt(x, 10);
		setLimit(n);
		const topN = new Set(sortedLocalities.slice(0, n));
		setRows(allRows.filter((r) => topN.has(r.locality)));
	};

	const ReportParameters = () => (
		<div>
			<div className="form-group row">
				<div className="col-sm-4">
					<label className="text-muted">Start Date</label>
					<DatePicker
						selected={startDate}
						onChange={(date) => {
							setStartDate(date);
							setDateChange(true);
						}}
					/>
				</div>
				<div className="col-sm-4">
					<label className="text-muted">End Date</label>
					<DatePicker
						selected={endDate}
						onChange={(date) => {
							setEndDate(date);
							setDateChange(true);
						}}
					/>
				</div>
				<div className="col-sm-4">
					<label className="text-muted">Limit (localities)</label>
					<input
						onChange={(e) => changeLimit(e.target.value)}
						type="number"
						value={limit}
						name="limit"
					/>
				</div>
			</div>
		</div>
	);

	const Grid = () => {
		const sortRows = (initialRows, sortColumn) => (rows) => {
			const key = sortColumn[0].columnKey;
			const direction = sortColumn[0].direction;
			const index = sortColumns.findIndex((l) => l.key === key);
			const columnDir = index >= 0 ? sortColumns[index].direction : "ASC";

			const comparer = (a, b) => {
				const l = key === "locality" ? a[key] : parseNumeric(a[key]);
				const r = key === "locality" ? b[key] : parseNumeric(b[key]);
				if (columnDir === "ASC") return l > r ? 1 : -1;
				return l < r ? 1 : -1;
			};

			setSortColumns(
				sortColumns.map((obj) =>
					obj.key === key
						? { ...obj, direction: columnDir === "ASC" ? "DESC" : "ASC" }
						: obj
				)
			);

			return direction === "NONE" ? initialRows : [...rows].sort(comparer);
		};

		return (
			<DataGrid
				className="rdg-light"
				columns={columna}
				rows={rows}
				rowGetter={(i) => rows[i]}
				rowsCount={rows.length || 0}
				sortColumn={sortColumns}
				onSortColumnsChange={(sortColumn) =>
					setRows(sortRows(rows, sortColumn))
				}
			/>
		);
	};

	return (
		<Layout title="Top Localities" description="Report">
			<ReportParameters />
			<br />
			{loading ? <Spinner /> : <Grid />}
			<br />
			<CSVLink
				data={rows}
				headers={headers()}
				filename={"57concrete-report.csv"}
			>
				Download report
			</CSVLink>
		</Layout>
	);
};

export default TopLocalities;
